import { useRouter } from 'next/router';
import { memo, type ReactNode, useCallback, useState } from 'react';

import { type NotificationFilter } from '@air/api/types';
import { NotificationsHeader, type NotificationsProps } from '@air/component-notifications';
import { cn } from '@air/utils-tailwind';

import { AirNotificationList } from '~/components/AirNotifications/components/AirNotificationList';
import { useGetNotificationSummary } from '~/components/AirNotifications/hooks/useGetNotificationSummary';
import { useMarkAllNotificationsAsRead } from '~/components/AirNotifications/hooks/useMarkAllNotificationsAsRead';
import { Routes } from '~/constants/routes';

export type DesktopNotificationsProps = Pick<NotificationsProps, 'className'> & {
  headerLeading?: ReactNode;
  onClose?: () => void;
  isUI2?: boolean;
};

export const DesktopNotifications = memo(({ className, headerLeading, onClose, isUI2 }: DesktopNotificationsProps) => {
  const router = useRouter();
  const [activeFilter, setActiveFilter] = useState<NotificationFilter | undefined>(undefined);
  const { markAllNotificationsAsRead } = useMarkAllNotificationsAsRead();
  const { data: notificationSummary } = useGetNotificationSummary();

  const onSettingsClick = useCallback(() => {
    onClose?.();
    router.push(Routes.account.notificationSettings);
  }, [onClose, router]);

  return (
    <div className={cn('flex ui2:h-full flex-col', className)} data-testid="NOTIFICATIONS">
      <NotificationsHeader
        leading={headerLeading}
        filters={[
          {
            id: 'all',
            name: 'All',
            isActive: !activeFilter,
            count: notificationSummary?.unread?.all,
          },
          {
            id: 'mentions',
            name: 'Mentions',
            isActive: activeFilter === 'mentions',
            count: notificationSummary?.unread?.mentions,
          },
          {
            id: 'following',
            name: 'Following',
            isActive: activeFilter === 'following',
            count: notificationSummary?.unread?.following,
          },
          {
            id: 'shortUrl',
            name: 'Links',
            isActive: activeFilter === 'shortUrl',
            count: notificationSummary?.unread?.shortUrl,
          },
        ]}
        onFilterClick={(filter) => {
          if (filter.id === 'all') {
            setActiveFilter(undefined);
          } else {
            setActiveFilter(filter.id as NotificationFilter);
          }
        }}
        onMarkAllAsRead={markAllNotificationsAsRead}
        onSettingsClick={onSettingsClick}
        isUI2={isUI2}
      />
      <AirNotificationList filter={activeFilter} onNotificationClick={onClose} />
    </div>
  );
});

DesktopNotifications.displayName = 'DesktopNotifications';
